import { useEffect, useState } from "react";
import api from "@/lib/api";
import AnimatedValue from "@/components/AnimatedValue";
import { BellRing, Send, TrendingUp, Clock } from "lucide-react";

/**
 * ReminderAnalytics — 3-stat row shown on the Calendar page proving that
 * reminders are lifting show-up rate. Reads `GET /api/reminders/analytics`.
 */
export default function ReminderAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reminders/analytics").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const nextAt = data?.next_reminder_at;
  const nextIn = nextAt ? formatIn(nextAt) : null;

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 mb-8" data-testid="reminder-analytics">
      <StatTile
        label="Reminders pending"
        value={data?.pending ?? "—"}
        sub={nextIn ? `Next fires ${nextIn}` : "Nothing queued"}
        icon={BellRing}
        testId="stat-reminders-pending"
      />
      <StatTile
        label="Sent · last 24h"
        value={data?.sent_today ?? "—"}
        sub="Emails + SMS dispatched"
        icon={Send}
        testId="stat-reminders-sent"
      />
      <StatTile
        label="Show-up rate · 30d"
        value={data?.show_up_rate == null ? "—" : `${data.show_up_rate}%`}
        sub={data?.total_past_appointments ? `${data.completed_past_appointments}/${data.total_past_appointments} attended` : "No past bookings"}
        icon={TrendingUp}
        testId="stat-showup-rate"
        accent="bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)]"
      />
      <StatTile
        label="Booked · past 30d"
        value={data?.total_past_appointments ?? "—"}
        sub="Total completed calendar slots"
        icon={Clock}
        testId="stat-booked-past"
      />
    </div>
  );
}

function StatTile({ label, value, sub, icon: Icon, testId, accent }) {
  return (
    <div className="brutalist-card p-5 flex flex-col justify-between min-h-[120px]" data-testid={testId}>
      <div className="flex items-start justify-between">
        <span className="section-label">{label}</span>
        <div className={`w-8 h-8 flex items-center justify-center peak-glow-accent ${accent || "bg-[var(--peak-ink)] text-[var(--peak-bg)]"}`}>
          <Icon className="w-4 h-4" strokeWidth={2.5} />
        </div>
      </div>
      <div>
        <div className="font-heading font-black text-3xl tracking-tighter">
          <AnimatedValue value={value} />
        </div>
        {sub && <div className="section-label mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function formatIn(iso) {
  try {
    const target = new Date(iso).getTime();
    const now = Date.now();
    const mins = Math.round((target - now) / 60000);
    if (mins <= 0) return "any moment";
    if (mins < 60) return `in ${mins}m`;
    const h = Math.round(mins / 60);
    if (h < 24) return `in ${h}h`;
    const d = Math.round(h / 24);
    return `in ${d}d`;
  } catch {
    return null;
  }
}
